import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetStateProfileListResponse } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'app/app.service';
import { SortingEvent } from 'app/key-ui/table/table.component';
import { OverviewListComponent } from 'app/pages/status/overview/components/list/list.component';
import { OverviewStateService } from './services/state.service';
import { AssetFilterService, AssetListItem, AssetStateService, ErrorLoggerService, LocalStore, MatchMediaService, MobileNavigationActionsService, NotificationService } from 'app/services';
import { FilterType } from 'app/services/assets/asset-filter.service';
import { BaseComponent, MapButton, OnAttach } from 'app/shared/components';
import { FilterPanelService } from 'app/shared/components/filter-panel/filter-panel.service';
import { CurrentClient } from 'app/shared/model';
import { ERRORS } from 'app/shared/model/errors.model';
import * as Bluebird from 'bluebird';
import { uniq } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { OverviewMapComponent } from './components/map/map.component';

export interface OverviewState {
    listLayoutType: 'list' | 'table';
    listSortField: string;
    listSortOrder: 'asc' | 'desc';
    mapHeight: number;
    listWidth: number;
    tableWidth: number;
    dateHighlightThreshold: number;
    dateFormatting: 'condensed' | 'full';
    detailsActiveTab: string;
    detailsLocation: 'bottom' | 'left' | 'right';
    filter: FilterChangeEvent[];
    showFilter: boolean;
}

export interface AssetsDictionary {
    [key: string]: AssetListItem;
}

export interface FilterChangeEvent {
    type: FilterType;
    id: string;
}

/** not happy about having to do this, but AOT demands it */
export const OverviewLocalStore = new LocalStore('overview-component');

const DEFAULT_SIDEBAR_WIDTH = '400px';

@Component({
    selector: 'key-status-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [{ provide: LocalStore, useValue: OverviewLocalStore }, FilterPanelService],
})
export class OverviewComponent extends BaseComponent implements OnInit, OnAttach {

    filterWidth = 250;

    state: OverviewState;
    errorMessage: string;

    isMobile: boolean;
    isAdvanced: boolean;

    assetsList: AssetListItem[] = [];
    assetListLoaded = false;

    selectedAsset: AssetListItem;

    client: CurrentClient;

    clientId: string;
    clientName: string;

    // cache asset state profiles for clients (could be multiple due to shared assets)
    stateProfiles: { [clientId: string]: AssetStateProfileListResponse } = {};

    sidebarWidth = DEFAULT_SIDEBAR_WIDTH;
    sidebarOpen = false;

    mapButtons: MapButton[];

    get page(): number {
        return (this.filter.offset / this.filter.limit) + 1;
    }

    /*
    * Expand the host element to entire viewport
    *
    * This aids with Safari where the map is not rendered to the full height of
    * the screen and only as high as the actual text content.
    */
    @HostBinding('class.d-flex') displayFlex = true;
    @HostBinding('class.flex-stretch') flexStretch = true;
    @HostBinding('class.flex-column') flexColumn = true;
    @HostBinding('class.h-100') fullHeight = true;

    /**
     * Used for updating the map when resizing happen.
     */
    @ViewChild(OverviewMapComponent) map: OverviewMapComponent;
    @ViewChild(OverviewListComponent, { static: false }) listComponent: OverviewListComponent;

    constructor(
        private matchMedia: MatchMediaService,
        public mobileActions: MobileNavigationActionsService,
        private error: ErrorLoggerService,
        public filter: AssetFilterService,
        private filterPanelService: FilterPanelService,
        private app: AppService,
        private store: LocalStore,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        public assetState: AssetStateService,
        public overviewState: OverviewStateService,
        private i18n: TranslateService,
        private cd: ChangeDetectorRef,
        private notify: NotificationService
    ) {

        super();

        this.state = this.store.watchState<OverviewState>('state', {
            listLayoutType: 'list',
            listSortField: null,
            listSortOrder: null,
            mapHeight: 300,
            tableWidth: 500,
            listWidth: 300,
            dateHighlightThreshold: 0,
            dateFormatting: 'condensed',
            // this is going to break between languages
            detailsActiveTab: this.i18n.instant('STATUS.DETAILS.ACTIVITY'),
            detailsLocation: 'bottom',
            filter: [],
            showFilter: false,
        });

        this.overviewState.setDetailLocation(this.state.detailsLocation);

        // making tablet matchMedia part of mobile for now. NOTE that iPad screen sizes are actually bigger than this check so they function as a Desktop view
        this.on(this.matchMedia.isMobileOrTablet, isMobile => {
            this.isMobile = isMobile;
        });


    }

    ngOnInit() {

        this.overviewState.selectedAsset$
            .pipe(takeUntil(this.destroyed))
            .subscribe(asset => {
                this.selectedAsset = asset;
                setTimeout(() => {
                    if (this.map) {
                        if (this.selectedAsset) {
                            this.map.zoomToAsset(this.selectedAsset);
                        } else {
                            this.map.resize();
                        }
                    }
                }, 100);
            });

        this.on(this.overviewState.detailLocation$, value => {
            // Set the location to 'null' initially which will remove the existing html from the page
            // before recreating it on the next tick. This gets around some flex issues.
            this.state.detailsLocation = null;
            this.cd.markForCheck();
            setTimeout(() => {
                this.state.detailsLocation = value;
                this.cd.markForCheck();
            });
        });

        this.assetState.onError$
            .pipe(takeUntil(this.destroyed))
            .subscribe(error => this.showError(error));

        // listen to changes on asset state, but retrieve the latest client on state changes too
        this.assetState.assets$
            .pipe(takeUntil(this.destroyed))
            .subscribe(assets => {
                this.updateAssetList(assets);
            });

        this.app.client$
            .pipe(takeUntil(this.destroyed))
            .subscribe(client => {
                this.updateClient(client);
            });

        this.on(this.filter.filters$, filters => {
            if (filters.point) {
                setTimeout(() => {
                    this.updateListSorting({
                        field: 'distance',
                        order: 'asc',
                    });
                }, 100);
            }

            filters.filters.forEach(value => {
                const old = this.filterPanelService.filters.filters[value.type];
                if (!old || !old[0] || old[0].id !== value.value) {
                    this.filterPanelService.updateFilterValues(value.type, [{ id: value.value, name: value.value }]);
                }
            });

        });




    }

    ngOnAttach() {
        // make sure we immediate resize the map when we get shown again, see https://git.kt1.io/key-telematics/fleet-ui/issues/261
        setTimeout(() => {
            if (this.map) { this.map.resize(); }
        });
    }

    async updateClient(client: CurrentClient) {
        if (client && this.clientId !== client.id) {

            this.isAdvanced = this.app.features.page.overview.layout !== 'basic';
            this.mapButtons = this.isAdvanced ? [] : [
                { id: 'assets', style: 'both', icon: 'search', title: this.i18n.instant('STATUS.OVERVIEW.ASSETS') },
            ];

            this.clientName = client.name;
            this.clientId = client.id;

            // reset filter
            this.listComponent?.reset();
            this.map?.reset();
            this.filterChanged(null);
            this.filterPanelService.resetFilters();
            // reassign assets to empty list so that previous assets doesn't persist between clients switch
            this.assetsList = [];

            await this.filter.loadClient(client.id);
            // the reset takes a while, make sure that all other filter changes are done AFTER the reset
            // completes or they will be applied on the previous client
            this.filter.setLimit(this.app.features.page.overview.pageSize || 300);
            this.state.filter = this.filter.filters.groups.map(f => ({ id: f.value, type: f.type }));

            // set loading to false for the next state update as soon as the assets loaded
            this.setLoaded(false);
        }
    }

    async updateAssetList(updates: AssetListItem[]): Promise<void> {

        if (updates) {

            await Bluebird.map(uniq(updates.map(x => x.owner.id)), async ownerId => {
                this.stateProfiles[ownerId] = await this.overviewState.getAssetStateProfiles(ownerId);
            });

            if (!this.assetListLoaded) {
                if (this.map) { this.map.reset(); }
                this.assetsList = this.assetState.assets;
            } else {
                this.assetsList = updates;
                if (this.selectedAsset) {
                    const asset = this.assetState.assetListIndex[this.selectedAsset.id];
                    if (asset) {
                        this.selectedAsset = { ...asset }; // have to change object ref for OnPush change detection to work
                    }
                }

                this.cd.markForCheck();
            }

            this.setLoaded(true);
        } else {
            this.assetListLoaded = false;
            this.listComponent?.reset();
            this.assetsList = [];
            this.setSelectedAsset(null);
        }
    }

    setLoaded(state: boolean) {
        this.assetListLoaded = state;
    }

    changeSearchTerm(filter: string) {
        this.filter.setFilter('name', filter || '');
        this.setLoaded(false);
    }

    changeListLayout(type: 'list' | 'table') {
        this.state.listLayoutType = type;
    }

    updateListWidth(width: number) {

        if (this.state.showFilter) {
            width = width - this.filterWidth;
        }

        if (this.state.listLayoutType === 'list') {
            this.state.listWidth = width;
        } else {
            this.state.tableWidth = width;
        }

        if (this.map) {
            this.map.resize();
        }
    }

    updateMapHeight(height: number) {
        this.state.mapHeight = height;

        if (this.map) {
            this.map.resize();
        }
    }

    showError(error: any) {
        this.error.trackException(error, [ERRORS.FORBIDDEN_ERROR]);
        this.errorMessage = error && this.notify.translateError(error);
    }

    setSelectedAsset(item: string): void {
        this.overviewState.navigateToAsset(item);
    }

    updateDateHighlightThreshold(threshold: number) {
        this.state.dateHighlightThreshold = threshold;
    }

    updateDateFormatting(formatting: 'condensed' | 'full') {
        this.state.dateFormatting = formatting;
    }

    filterChanged(filter: FilterChangeEvent[]) {
        this.state.filter = filter || [];
        this.setLoaded(false);
    }

    updateDetailsActiveTab(name: string) {
        this.state.detailsActiveTab = name;
    }

    updateListSorting(sort: SortingEvent) {
        this.state.listSortField = sort.field;
        this.state.listSortOrder = sort.order as 'asc' | 'desc';
    }

    updatePage(page: number) {
        this.filter.setOffset((page - 1) * this.filter.limit);
        this.setLoaded(false);
    }

    toggleFilterPanel(visible: boolean) {
        this.state.showFilter = visible;
        this.sidebarWidth = visible ? '600px' : DEFAULT_SIDEBAR_WIDTH;
    }

    getListSize(): number {
        let width = this.state.listLayoutType === 'list' ? this.state.listWidth : this.state.tableWidth;
        if (this.state.showFilter) {
            width = width + this.filterWidth;
        }
        return width;
    }

    toggleSideBar(visible: boolean) {
        this.sidebarOpen = visible;
        if (this.sidebarOpen) {
            setTimeout(() => {
                this.listComponent?.toggleSearchbar(true);
            }, 100);
        }
    }

}
